// A placed butterfly valve changed in place (3.19.0): lever or gearbox, how far open, plan or side. The valve is drawn
// again from the library (src/library/butterflyValves.ts), centred where it is and along its ends as they are now.
// 3.22.0: drawn at its true ERA size. When the new valve is longer or shorter, its ends move along the pipe axis and the
// pipes on them follow (the pipe deriver draws each pipe again from its ends). The parts keep the colour the person
// gave the valve. One command, one undo step.
// Pure: no store, no UI. tests/pipeInsert.test.ts covers it.
import type { Vec } from "../core/geometry";
import type { Doc, Entity } from "../doc/entities";
import { makeDeleteCommand, makeUngroupCommand, type Command } from "../doc/commands";
import { newId } from "../doc/document";
import { butterflyRowFor, clampPct, hasOperator, makeButterflyItem, operatorSizes, type ButterflyInfo, type ButterflySpec } from "../library/butterflyValves";
import type { FittingPortData } from "./api";
import { partsInColour, runColourOf } from "./runColour";

/**
 * meta.butterfly of a placed butterfly valve, or null. A valve placed before 3.22.0 has `open` (yes or no) and no
 * `openPct`: it reads as 100 % or 0 %.
 */
export function butterflyInfoOf(e: Entity | undefined): ButterflyInfo | null {
  const b = e?.type === "group" ? (e.meta?.butterfly as (Partial<ButterflyInfo> & { DN?: unknown }) | undefined) : undefined;
  if (!b || typeof b.DN !== "number" || (b.operator !== "lever" && b.operator !== "gearbox")) return null;
  const openPct = typeof b.openPct === "number" ? clampPct(b.openPct) : b.open === false ? 0 : 100;
  return { ...(b as ButterflyInfo), openPct, open: openPct > 0, view: b.view === "side" ? "side" : "plan" };
}

/**
 * ONE command that draws butterfly valve `id` again with `change` (the rest as it is). A sentence instead when it
 * cannot: ERA has no such valve in that size, or the valve has lost its ends.
 */
export function butterflyCommand(doc: Doc, id: string, change: Partial<ButterflySpec>): Command | string {
  const g = doc.entities[id];
  const info = butterflyInfoOf(g);
  if (!g || g.type !== "group" || !info) return "Select a butterfly valve.";
  const spec: ButterflySpec = {
    operator: change.operator ?? info.operator,
    openPct: clampPct(change.openPct ?? info.openPct),
    view: change.view ?? info.view
  };
  const fromEra = info.maker === "ERA";
  if (fromEra && spec.operator === info.operator && spec.openPct === info.openPct && spec.view === info.view) return "The valve is like that already.";
  const row = butterflyRowFor(info.DN);
  if (!row) return `ERA has no butterfly valve of DN${info.DN}. Lever valves are ${operatorSizes("lever")}; gearbox valves are ${operatorSizes("gearbox")}.`;
  if (!hasOperator(row, spec.operator)) {
    return spec.operator === "gearbox"
      ? `ERA has no gearbox valve in DN${info.DN}. Gearbox valves are ${operatorSizes("gearbox")}.`
      : `ERA has no lever valve in DN${info.DN}. Lever valves are ${operatorSizes("lever")}.`;
  }
  const placed = g.meta?.ports as FittingPortData[] | undefined;
  if (!Array.isArray(placed) || placed.length !== 2) return "This valve has no ends that TraceCAD can read. Place a new valve.";

  const item = makeButterflyItem(row, spec);
  const ng = item.entities.find((e) => e.type === "group")!;
  const lib = ng.meta!.ports as FittingPortData[];
  // the library valve onto the placed valve: its centre on the middle of the placed ends, its axis along them, at its
  // own size (3.22.0: no stretch; the pipes follow the ends)
  const P0 = placed[0]!.p;
  const P1 = placed[1]!.p;
  const len = Math.hypot(P1.x - P0.x, P1.y - P0.y);
  if (len < 1e-6) return "This valve has no length. Place a new valve.";
  const u = { x: (P1.x - P0.x) / len, y: (P1.y - P0.y) / len };
  const n = { x: -u.y, y: u.x };
  const c = { x: (P0.x + P1.x) / 2, y: (P0.y + P1.y) / 2 };
  const mp = (p: Vec): Vec => ({ x: c.x + p.x * u.x + p.y * n.x, y: c.y + p.x * u.y + p.y * n.y });
  const turn = (v: Vec): Vec => ({ x: v.x * u.x + v.y * n.x, y: v.x * u.y + v.y * n.y });
  const deg = (Math.atan2(u.y, u.x) * 180) / Math.PI;

  const kids = g.children.map((cid) => doc.entities[cid]).filter((e): e is Entity => !!e);
  const layerId = kids[0]?.layerId ?? g.layerId;
  const parts = partsInColour(item.entities.filter((e) => e.type !== "group"), runColourOf(doc, id));
  const entities: Entity[] = parts.map((e) => {
    const nid = newId();
    if (e.type === "polyline") return { ...e, id: nid, layerId, points: e.points.map(mp) };
    if (e.type === "arc") return { ...e, id: nid, layerId, center: mp(e.center), startDeg: e.startDeg + deg };
    return { ...e, id: nid, layerId };
  });
  const ports: FittingPortData[] = lib.map((pt) => ({ p: mp(pt.p), dir: turn(pt.dir), size: pt.size }));
  const meta = { ...(g.meta ?? {}), fitting: structuredClone(ng.meta!.fitting), butterfly: structuredClone(ng.meta!.butterfly), pivot: ports[0]!.p, ports };
  const groupZ = { layerId: g.layerId, index: (doc.zOrder[g.layerId] ?? []).indexOf(id) };
  const commands: Command[] = [makeUngroupCommand(doc, id)];
  if (g.children.length) commands.push(makeDeleteCommand(doc, [...g.children]));
  commands.push({ kind: "addEntities", entities, z: entities.map(() => ({ layerId, index: -1 })) });
  commands.push({
    kind: "group",
    group: { ...structuredClone(g), children: entities.map((e) => e.id), meta },
    z: groupZ,
    children: entities.map((e) => ({ id: e.id, z: { layerId, index: -1 } }))
  });
  return { kind: "batch", label: "butterfly valve", commands };
}
